import { backUrl } from "../utils/backURL";
import { getFromStorage } from "../utils/storage";
import { withAuthCredentials } from "../utils/authFetchOptions";

/**
 * Calls Perlita API through backend proxy to avoid CORS issues
 * @param {string} prompt - The prompt/instruction to send to Perlita
 * @param {Array} messages - Array of conversation messages
 * @param {Object} options - Additional options (model, temperature, system_instructions)
 * @returns {Promise<Object>} The response from Perlita
 */
export const callPerlita = async (prompt, messages = [], options = {}) => {
  try {
    const modelValue = options.model || "us.anthropic.claude-haiku-4-5-20251001-v1:0";
    const modelArray = Array.isArray(modelValue) ? modelValue : [modelValue];

    const perlaData = {
      models: modelArray,
      prompt: prompt,
      temperature: options.temperature || 0.7,
      system_instructions: options.system_instructions || "",
      ...(messages.length > 0 && { messages: messages }),
      ...(options.files && { files: options.files }),
    };

    const token = options.token || getFromStorage("mantis_app")?.token;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 60000);

    const response = await fetch(`${backUrl}/perlita/process`, withAuthCredentials({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(perlaData),
      signal: controller.signal,
    }));

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error! status: ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error calling Perlita:", error);
    if (error.name === "AbortError") {
      throw new Error("Request timeout - el análisis tomó demasiado tiempo");
    }
    throw error;
  }
};

/**
 * Formats a conversation as a single text string for analysis
 * @param {Array} messages - Array of message objects
 * @returns {string} Formatted conversation text
 */
export const formatConversationAsText = (messages) => {
  const sortedMessages = (messages || [])
    .sort((a, b) => {
      const dateA = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
      const dateB = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
      return dateA - dateB;
    });

  return sortedMessages
    .map((message, index) => {
      const sender =
        message.sender === "USER"
          ? "Usuario"
          : message.sender === "AGENT"
          ? "Agente"
          : message.sender === "MANTIS"
          ? "Mantis"
          : message.sender || "Desconocido";

      const timestamp = message.dateCreated
        ? new Date(message.dateCreated).toLocaleString("es-ES")
        : "Sin fecha";

      const content =
        message.content?.text || message.content?.html || "Sin contenido";

      return `${index + 1}. [${sender}] (${timestamp}): ${content}`;
    })
    .join("\n\n");
};
